import React, { useId } from 'react';
import useDimensions from 'react-cool-dimensions';

interface BigValueProps {
  style?: React.CSSProperties;
  children: React.ReactNode;
  /** Multiplier for the max rendered height cap (default 1). */
  scaleMultiplier?: number;
}

/**
 * Renders the contents of the component, and dynamically scales them to fill
 * the parent component.
 *
 * This works by rendering a hidden copy of the children to measure their size,
 * rendering a container that is `{width: '100%', height: '100%'}` to fill the
 * parent, then finally rendering the actual values and applying a tranform
 * to scale the contents to fit the parent.
 */
const BigValue: React.FC<BigValueProps> = ({ style, children, scaleMultiplier = 1 }) => {
  const { height: parentHeight, width: parentWidth, observe: parentRef } = useDimensions();
  const { height: childHeight, width: childWidth, observe: childRef } = useDimensions();
  const testId = useId();

  let scale = 1;
  if (childHeight && childWidth && parentHeight && parentWidth) {
    const maxHeightPx = window.innerHeight * 0.15 * scaleMultiplier;
    const maxScaleForHeight = maxHeightPx / childHeight;

    // Scale to fit container, but cap at 15vh max height
    scale = Math.min(parentHeight / childHeight, parentWidth / childWidth, maxScaleForHeight) * 0.9;
  }

  // We want to scale the child element so it perfectly fills the size of the
  // parent element.
  return (
    <div
      style={{ ...style, width: '100%', height: '100%' }}
      className="flex items-center justify-center"
      ref={parentRef}
    >
      {/* Hidden element for measuring child dimensions */}
      <div ref={childRef} style={{ position: 'absolute', visibility: 'hidden' }}>
        {children}
      </div>

      {/* Scaled value */}
      <div
        style={{
          transformOrigin: 'center',
          transition: 'transform 0',
          transform: `scale(${scale})`,
        }}
      >
        <span data-testid={`big-value-text-${testId}`}>{children}</span>
      </div>
    </div>
  );
};

export default React.memo(BigValue);
